/**
 * AI 수리 루프 — 결정 #30(자동 디버깅/수리) 3단계. 결정적 사전패스(무료) → 검증 →
 * AI 수리 → 재검증을 **반복 상한**과 **무진전 감지**로 감싼다. 토큰(사용자 비용)이
 * 무한히 새지 않게 하는 것이 핵심 안전장치다.
 *
 * 플랫폼 중립(결정 #10): AiGateway는 core 포트라 mock으로 테스트한다. 검증 로직은
 * 도메인별로 주입(validate)한다 — 게임 `.rqg` 문서는 parseRoqeetGameSource, 스크립트는
 * analyzeBalance 등. UI/스트리밍/diff 표시는 web이 담당한다.
 *
 * 대화형(결정: 대화형 수정): instruction을 주면 "이 부분을 이렇게 바꿔줘"가 되고,
 * 안 주면 순수 자동 수리("오류를 고쳐줘")가 된다. messages를 누적·반환하므로 web이
 * 후속 턴을 이어붙일 수 있다.
 */
import type { ChatTurn, AiGateway } from "../ports/ai.js";
import type { ProviderProfile, SamplingParams } from "../domain/index.js";
import { autoRepair, type RepairLang } from "./balance.js";

/** 한 번의 수리 진행 단계(UI 타임라인/디버깅용). */
export interface RepairStep {
  iteration: number;
  phase: "pre-pass" | "validate" | "ai" | "aborted" | "error";
  /** 이 단계 직후의 소스. */
  source: string;
  /** 이 단계에서 본 검증 오류(있으면). */
  errors: string[];
  note?: string;
}

export type RepairStoppedReason =
  | "valid" // 검증 통과(성공)
  | "max-iterations" // 반복 상한 도달
  | "no-progress" // 같은 오류 반복(진전 없음) → 중단
  | "aborted" // 사용자 취소
  | "error"; // AI 호출 실패

export interface RepairOutcome {
  ok: boolean;
  /** 최종(가장 나은) 소스. */
  source: string;
  iterations: number;
  steps: RepairStep[];
  /** 끝까지 남은 검증 오류. */
  remainingErrors: string[];
  stoppedReason: RepairStoppedReason;
  /** 누적 대화(system/user/assistant). 후속 대화형 턴에 이어붙일 수 있다. */
  messages: ChatTurn[];
}

export interface RepairLoopOptions {
  /** 수리 대상 소스(JSON/JS/Lua 등). */
  source: string;
  /** 결정적 사전패스 문법(괄호/Lua end). */
  lang: RepairLang;
  /** 도메인 검증: 오류 메시지 배열을 반환(빈 배열 = 유효). 던지지 말 것. 비동기 허용
   *  (예: Acorn 등 파서를 lazy-load하는 web 검증기). */
  validate: (source: string) => string[] | Promise<string[]>;
  /** AI 게이트웨이(결정 #7 포트). complete만 사용(비스트리밍 1회). */
  gateway: Pick<AiGateway, "complete">;
  profile: ProviderProfile;
  model: string;
  params?: SamplingParams;
  /** AI에게 포맷/스키마/규칙을 가르치는 시스템 프롬프트. */
  systemPrompt: string;
  /** 대화형 편집 지시(선택). 없으면 순수 자동 수리. */
  instruction?: string;
  /** 이어가는 대화가 있으면 그 메시지들(없으면 systemPrompt로 시작). */
  priorMessages?: ChatTurn[];
  /** 최대 AI 왕복 횟수(기본 4). 토큰 소모 상한. */
  maxIterations?: number;
  signal?: AbortSignal;
  /** 단계별 진행 콜백(UI 타임라인). */
  onStep?: (step: RepairStep) => void;
}

const DEFAULT_MAX_ITERATIONS = 4;

/**
 * AI 응답 텍스트에서 코드를 추출한다. 모델이 흔히 ```lang … ``` 펜스로 감싸므로,
 * 펜스가 있으면 가장 큰 블록을, 없으면 전체를 트림해 돌려준다(순수·테스트 가능).
 */
export function extractCode(text: string): string {
  const fence = /```[ \t]*([\w-]*)[ \t]*\r?\n([\s\S]*?)```/g;
  let best: string | null = null;
  let m: RegExpExecArray | null;
  while ((m = fence.exec(text)) !== null) {
    const body = m[2] ?? "";
    if (best === null || body.length > best.length) best = body;
  }
  return (best ?? text).trim();
}

/** 검증 오류 배열의 안정적 서명(무진전 감지용). 순서 무관 비교. */
function errorSignature(errors: string[]): string {
  return errors.slice().sort().join("\u0001");
}

/** AI에게 보낼 사용자 메시지를 만든다(현재 소스 + 오류 + 선택적 지시). */
function buildUserMessage(source: string, errors: string[], instruction?: string): string {
  const parts: string[] = [];
  if (instruction) {
    parts.push(`다음 요청을 반영해 코드를 수정하세요:\n${instruction}`);
  } else {
    parts.push("아래 코드의 오류를 고쳐, 전체 코드를 다시 출력하세요.");
  }
  if (errors.length) {
    parts.push(`검출된 문제:\n${errors.map((e) => `- ${e}`).join("\n")}`);
  }
  parts.push(
    "규칙: 설명 없이 수정된 전체 코드만, 하나의 코드블록(```)으로 출력하세요. 부분 코드·생략(...) 금지.",
  );
  parts.push("현재 코드:\n```\n" + source + "\n```");
  return parts.join("\n\n");
}

/**
 * 수리 루프를 실행한다(결정 #30). 흐름:
 *  1) 결정적 사전패스(autoRepair) — 무료로 닫히지 않은 괄호 등 선처리.
 *  2) 검증 — 유효하고 지시도 없으면 즉시 종료(토큰 0).
 *  3) 반복: AI 호출 → 코드 추출 → 사전패스 → 검증.
 *     - 유효 → 성공 종료.
 *     - 같은 오류 반복 → "무진전"으로 중단(토큰 보호).
 *     - 상한 도달 → 중단.
 */
export async function runRepairLoop(opts: RepairLoopOptions): Promise<RepairOutcome> {
  const max = opts.maxIterations ?? DEFAULT_MAX_ITERATIONS;
  const steps: RepairStep[] = [];
  const emit = (s: RepairStep) => {
    steps.push(s);
    opts.onStep?.(s);
  };

  let current = opts.source;

  // 1) 결정적 사전패스(무료).
  const pre = autoRepair(current, opts.lang);
  if (pre.applied.length) {
    current = pre.fixed;
    emit({
      iteration: 0,
      phase: "pre-pass",
      source: current,
      errors: [],
      note: `결정적 사전 수리 ${pre.applied.length}건`,
    });
  }

  // 2) 초기 검증.
  let errors = await opts.validate(current);
  emit({ iteration: 0, phase: "validate", source: current, errors });

  // 유효하고 편집 지시도 없으면 AI 호출 없이 종료(토큰 0).
  if (errors.length === 0 && !opts.instruction) {
    return {
      ok: true,
      source: current,
      iterations: 0,
      steps,
      remainingErrors: [],
      stoppedReason: "valid",
      messages: opts.priorMessages ?? [],
    };
  }

  const messages: ChatTurn[] =
    opts.priorMessages && opts.priorMessages.length
      ? [...opts.priorMessages]
      : [{ role: "system", content: opts.systemPrompt }];

  let lastSig: string | null = null;

  for (let i = 1; i <= max; i++) {
    if (opts.signal?.aborted) {
      emit({ iteration: i, phase: "aborted", source: current, errors });
      return {
        ok: false,
        source: current,
        iterations: i - 1,
        steps,
        remainingErrors: errors,
        stoppedReason: "aborted",
        messages,
      };
    }

    // 첫 반복에만 편집 지시를 싣는다(이후는 순수 오류 수정).
    const userMsg = buildUserMessage(current, errors, i === 1 ? opts.instruction : undefined);
    messages.push({ role: "user", content: userMsg });

    let text: string;
    try {
      const result = await opts.gateway.complete(
        {
          messages,
          model: opts.model,
          params: opts.params ?? {},
          stream: false,
          ...(opts.signal ? { signal: opts.signal } : {}),
        },
        opts.profile,
      );
      text = result.text;
    } catch (e) {
      const note = e instanceof Error ? e.message : String(e);
      emit({ iteration: i, phase: "error", source: current, errors, note });
      return {
        ok: false,
        source: current,
        iterations: i - 1,
        steps,
        remainingErrors: errors,
        stoppedReason: "error",
        messages,
      };
    }
    messages.push({ role: "assistant", content: text });

    // 추출 + AI 출력에도 결정적 사전패스(작은 모델의 괄호 누락 보정).
    const extracted = extractCode(text);
    current = autoRepair(extracted, opts.lang).fixed;
    emit({ iteration: i, phase: "ai", source: current, errors: [] });

    errors = await opts.validate(current);
    emit({ iteration: i, phase: "validate", source: current, errors });

    if (errors.length === 0) {
      return {
        ok: true,
        source: current,
        iterations: i,
        steps,
        remainingErrors: [],
        stoppedReason: "valid",
        messages,
      };
    }

    // 무진전 감지: 직전과 같은 오류 집합이면 중단(토큰 보호).
    const sig = errorSignature(errors);
    if (sig === lastSig) {
      return {
        ok: false,
        source: current,
        iterations: i,
        steps,
        remainingErrors: errors,
        stoppedReason: "no-progress",
        messages,
      };
    }
    lastSig = sig;
  }

  return {
    ok: false,
    source: current,
    iterations: max,
    steps,
    remainingErrors: errors,
    stoppedReason: "max-iterations",
    messages,
  };
}
